import React, { useCallback } from 'react';
import { GIRA_APOLLO_CLIENT } from '@atlassian/jira-apollo-multiple-clients/src/common/constants.tsx';
import {
	ApolloClientProvider,
	resetCache,
} from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { usePublishFormContext } from '@atlassian/jira-issue-create-form-context/src/utils.tsx';
import { useIssueRestoreMetaService } from '@atlassian/jira-issue-create-restore/src/services/issue-restore-meta/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import messages from './common/messages.tsx';
import type { Payload } from './common/types/index.tsx';
import { NavigationDiscardChangesContainer } from './controllers/navigation-discard-changes/index.tsx';
import { useIssueCreateFromAnywhereContext } from './controllers/use-issue-create-from-anywhere-context/index.tsx';
import { usePublishUseTriggerIssueCreateModalChange } from './controllers/use-publish-use-trigger-issue-create-modal-change/index.tsx';
import { IssueRestoreWrapper } from './ui/issue-restore-wrapper/index.tsx';
import IssueCreateModal from './ui/modal/index.tsx';
import type { CreateModalProps } from './ui/modal/types.tsx';

const onResetApolloCache = () => resetCache(GIRA_APOLLO_CLIENT);

const IssueCreateAppWithApolloProvider = ({ onError }: { onError?: () => void }) => {
	const [{ isModalOpen }] = useTriggerIssueCreateModal();
	const { deleteIssueRestoreMeta } = useIssueRestoreMetaService();

	const _onError = useCallback(() => {
		onError?.();
		deleteIssueRestoreMeta();
	}, [onError, deleteIssueRestoreMeta]);

	const { giraClient } = useIssueCreateFromAnywhereContext();

	usePublishUseTriggerIssueCreateModalChange();

	usePublishFormContext();

	const renderIssueCreateModal = ({
		onTransformCreateFormProps,
		setShouldRestore,
	}: CreateModalProps = {}) => {
		if (isModalOpen) {
			return (
				<IssueCreateModal
					onTransformCreateFormProps={onTransformCreateFormProps}
					setShouldRestore={setShouldRestore}
					onResetApolloCache={onResetApolloCache}
				/>
			);
		}
		return null;
	};

	if (fg('jira_ai_issue_create_site_user_preference')) {
		// don't render IssueCreate unless the modal is open
		// this fixes problems with loading the correct data for pre-selected sites
		return (
			isModalOpen && (
				<JSErrorBoundary
					id="issue-create-app"
					packageName="jiraIssueCreate"
					fallback="flag"
					onError={_onError}
					sendToPrivacyUnsafeSplunk
				>
					<ApolloClientProvider client={giraClient}>
						<NavigationDiscardChangesContainer>
							<IssueRestoreWrapper>
								{({ onTransformCreateFormProps, setShouldRestore }) => (
									<JSErrorBoundary
										id="issue-create-app-children"
										packageName="jiraIssueCreate"
										fallback="flag"
										onError={_onError}
										sendToPrivacyUnsafeSplunk
									>
										{renderIssueCreateModal({
											onTransformCreateFormProps,
											setShouldRestore,
										})}
									</JSErrorBoundary>
								)}
							</IssueRestoreWrapper>
						</NavigationDiscardChangesContainer>
					</ApolloClientProvider>
				</JSErrorBoundary>
			)
		);
	}
	return (
		<JSErrorBoundary
			id="issue-create-app"
			packageName="jiraIssueCreate"
			fallback="flag"
			onError={_onError}
			sendToPrivacyUnsafeSplunk
		>
			<ApolloClientProvider client={giraClient}>
				<NavigationDiscardChangesContainer>
					<IssueRestoreWrapper>
						{({ onTransformCreateFormProps, setShouldRestore }) => (
							<JSErrorBoundary
								id="issue-create-app-children"
								packageName="jiraIssueCreate"
								fallback="flag"
								onError={_onError}
								sendToPrivacyUnsafeSplunk
							>
								{renderIssueCreateModal({
									onTransformCreateFormProps,
									setShouldRestore,
								})}
							</JSErrorBoundary>
						)}
					</IssueRestoreWrapper>
				</NavigationDiscardChangesContainer>
			</ApolloClientProvider>
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { Payload };

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports
export { ApolloClientProvider, IssueCreateModal, messages };

export default IssueCreateAppWithApolloProvider;
